import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowUp } from "lucide-react";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const [isVisible, setIsVisible] = useState(false);

  // This effect automatically scrolls the window to the top on every page change.
  useEffect(() => {
    // Prevent browser from restoring scroll position automatically
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      document.documentElement.scrollTo({ top: 0, left: 0, behavior: "instant" });
      document.body.scrollTo({ top: 0, left: 0, behavior: "instant" });
      const dashboardArea = document.getElementById("dashboard-scroll-area");
      if (dashboardArea) {
        dashboardArea.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }
    };

    // 1st attempt synchronously
    scrollToTop();

    // Multiple asynchronous attempts due to React.lazy / Suspense delays
    const timeouts = [10, 50, 100, 300, 500].map((ms) =>
      setTimeout(scrollToTop, ms)
    );

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [pathname]);

  // This effect manages the visibility of the "Back to Top" button.
  useEffect(() => {
    const toggleVisibility = () => {
      // Show the button if the user has scrolled more than 300px down.
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    // Listen for scroll events.
    window.addEventListener("scroll", toggleVisibility);

    // Clean up the listener when the component is removed.
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  // This function is called when the user clicks the button to scroll up smoothly.
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  return (
    <>
      {/* The button is only rendered if isVisible is true. */}
      {isVisible && (
        <Button
          onClick={scrollToTop}
          // The position has been adjusted to sit comfortably above other floating elements like a chat widget, sticky checkout bars, and mobile footer.
          className="fixed bottom-[12rem] lg:bottom-24 right-4 z-40 w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 animate-fade-in"
          size="icon"
        >
          <ArrowUp className="h-5 w-5" />
        </Button>
      )}
    </>
  );
};

export default ScrollToTop;
